/* eslint-disable react/no-unknown-property */
import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  SegmentedControl,
  SegmentedControlItem,
  TextTitle,
  TextOverline,
  TextBody,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';

import { detectPlatform, type Platform } from './platform';
import { getShortcutsForPlatform } from './shortcuts-model';

import './keyboard-shortcuts-panel.scss';

// pro-studio i18n: components use useTranslation() (default ns = pro-studio/common)
// and a full `scope/file:` KEY prefix — i18n.ts `read()` splits the namespace on
// '/', so a bare 'keyboard-shortcuts' resolves to fileName=undefined and fails to
// load (the panel would then render raw keys like "rows.copy"). Prefix every key.
const KB_NS = 'pro-studio/keyboard-shortcuts';

// PSTUDIO-3623: right-sidebar help panel listing the real editor shortcuts.
// Mirrors the Expanded Cell View panel shape (observer, fixed 500px column).
// Default tab = the user's OS; the SegmentedControl lets them peek at the other.
// The list is the exact mirror of the real hotkeys — see shortcuts-model.ts.
export const KeyboardShortcutsPanel: React.FC = observer(() => {
  const [t] = useTranslation();
  const [platform, setPlatform] = useState<Platform>(() => detectPlatform());
  const sections = getShortcutsForPlatform(platform);

  return (
    <div
      test-id="ps-keyboard-shortcuts-panel"
      className="c-keyboard-shortcuts-panel"
    >
      <div className="c-keyboard-shortcuts-panel__header">
        {/* Figma Header/H5 = Gordita Regular 20/32; TextTitle size="small" =
            20px, weight normal — the matching DS variant. */}
        <TextTitle size="small">{t(`${KB_NS}:title`)}</TextTitle>
      </div>

      <SegmentedControl
        test-id="ps-keyboard-shortcuts-platform-toggle"
        className="c-keyboard-shortcuts-panel__toggle"
        value={platform}
        onChange={(e: CustomEvent<{ value: string }>) =>
          setPlatform(e.detail.value as Platform)
        }
      >
        <SegmentedControlItem value="mac">
          {t(`${KB_NS}:platform.mac`)}
        </SegmentedControlItem>
        <SegmentedControlItem value="windows">
          {t(`${KB_NS}:platform.windows`)}
        </SegmentedControlItem>
      </SegmentedControl>

      <div className="c-keyboard-shortcuts-panel__list">
        {sections.map((section) => (
          <section
            key={section.id}
            className="c-keyboard-shortcuts-panel__section"
          >
            {/* Figma Body/Overline = Gordita Medium 9/16 uppercase, muted;
                TextOverline is exactly that (5xsmall, medium, uppercase,
                secondary) — no CSS text-transform needed. */}
            <TextOverline className="c-keyboard-shortcuts-panel__section-title">
              {t(`${KB_NS}:${section.titleKey}`)}
            </TextOverline>
            {section.rows.map((row) => (
              <div key={row.id} className="c-keyboard-shortcuts-panel__row">
                <TextBody
                  size="small"
                  className="c-keyboard-shortcuts-panel__desc"
                >
                  {t(`${KB_NS}:${row.descriptionKey}`)}
                </TextBody>
                <span className="c-keyboard-shortcuts-panel__keys">
                  {row.keys}
                </span>
              </div>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
});
